// ---------------------------------------------------------------------------
// The original scroll-reveal: adds `.visible` the first time an element enters
// the viewport, then stops observing it.
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState } from 'react';

export function useReveal() {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || isVisible) return undefined;

    // Elements already on screen at mount should not wait for a scroll event.
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [isVisible]);

  return { ref, isVisible, className: isVisible ? 'reveal visible' : 'reveal' };
}

export function Reveal({ as: Tag = 'div', className = '', children, ...rest }) {
  const { ref, isVisible } = useReveal();
  return (
    <Tag ref={ref} className={`${className} reveal${isVisible ? ' visible' : ''}`.trim()} {...rest}>
      {children}
    </Tag>
  );
}
